// Modal-style dialog primitive (phase 2.1.5). Replaces 15+ inline Modal +
// backdrop + content-card hand-rolls across the codebase.
//
// Default chrome lifted from MyPassport's BadgeDetailPanel (the modal that
// reads as the most polished one in the app):
//   - Frosted-glass container (backdrop-filter blur on web,
//     <BlurView intensity={80} tint="light"> on native).
//   - 40% black backdrop overlay (lighter than the old 50%).
//   - 20px radius, generous padding (28 H × 40 top / 32 bottom).
//   - Optional close-button affordance in the top-right corner.
//
// Two layouts:
//   - "centered": alert / confirmation. Fade animation, 90% wide, centered;
//     `size="wide"` lets a detail dialog grow wider on a tablet or the web.
//     Use for LogoutModal, DeleteAccountModal, Disable2FAModal, badge details,
//     etc.
//   - "bottom":   anchored to bottom edge. Slide animation, top corners
//     rounded. Use for InsuranceInfoModal, IntentPickerModal, etc.
//
// Important: this is a **modal**, not a real native sheet. A real sheet
// (iOS-style with drag handle, snap points, gesture-driven dismiss,
// partial reveal) is reserved as <BottomSheet>, not yet built.
//
// Backdrop tap dismisses by default. Pass `dismissOnBackdropTap={false}`
// for required-action dialogs.
//
// Every layout lifts above the on-screen keyboard, and the centered and
// bottom layouts stop at 90% of the screen height: a ScrollView inside the
// content shrinks to fit, so callers don't need their own maxHeight or
// KeyboardAvoidingView. The bottom layout keeps its last row clear of the
// home indicator and caps its width on tablets and the web.
//
// Caller provides their own typography + buttons inside `children`.
import { createContext, useCallback, useState, type ReactNode } from "react";
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { BlurView } from "expo-blur";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { ToastHost } from "../hooks/useToast";
import { radii, semanticColors, spacing } from "../theme/tokens";
import { CLOSE_BUTTON_SIZE, CloseButton } from "./CloseButton";

// "fullscreen" fills the viewport edge-to-edge (safe-area aware) — the right
// presentation for immersive surfaces like a chat thread on a phone, where a
// centered card is too cramped. Slide animation, no radius.
type Layout = "centered" | "bottom" | "fullscreen";
type CloseButtonOption = false | "minimal";

type Props = {
  visible: boolean;
  onClose: () => void;
  layout?: Layout;
  /**
   * A centered dialog's width on a tablet or the web: `wide` for a detail
   * dialog or a long form. On a phone both are 90% of the screen.
   */
  size?: "default" | "wide";
  /** Dialog surface treatment. Solid is useful when the dialog contains cards. */
  surface?: "frosted" | "solid";
  animationType?: "none" | "fade" | "slide";
  /** Top-right close affordance.
   *  - `false` (default): no close button. Use when the dialog has its
   *    own action buttons (Cancel + Confirm) that handle dismissal.
   *  - `"minimal"`: a subtle ✕ in the top-right, no background. Use for
   *    info / detail dialogs (badge details, info popups). */
  closeButton?: CloseButtonOption;
  /** What a screen reader says for the close button. Defaults to "Close". */
  closeAccessibilityLabel?: string;
  /** Stable analytics identifier for a press on the close button. */
  closeAction?: string;
  /** Default `true`. Pass `false` to require an explicit action to dismiss. */
  dismissOnBackdropTap?: boolean;
  /** Optional sizing overrides for wider detail dialogs that still need shared chrome. */
  containerStyle?: StyleProp<ViewStyle>;
  /** Optional surface overrides for scrollable dialogs that need bounded height. */
  surfaceStyle?: StyleProp<ViewStyle>;
  /** Optional padding/layout overrides for custom scrollable dialog content. */
  contentStyle?: StyleProp<ViewStyle>;
  /** Optional test hook for the dismissible backdrop. */
  backdropTestID?: string;
  /**
   * Analytics label recorded when the backdrop dismisses the dialog, a
   * static snake_case descriptor such as `dismiss_info_sheet_backdrop`.
   */
  backdropPressLabel?: string;
  testID?: string;
  children: ReactNode;
};

/**
 * Lets a left-aligned header ask the dialog to start its content level with
 * the close (#3848), instead of below the gap a centred illustration needs.
 * The header passes `true` while it is shown and `false` when it goes.
 */
export const DialogChromeContext = createContext<{
  closeButtonShown: boolean;
  setTitleBesideClose: (beside: boolean) => void;
}>({
  closeButtonShown: false,
  setTitleBesideClose: () => {},
});

// Where a left title starts when it shares its row with the close.
const TITLE_BESIDE_CLOSE_TOP = 24;
/** The height of one line of a dialog title (DialogTitleText). */
export const DIALOG_TITLE_LINE_HEIGHT = 25;
// Where the close sits when no left title shares its row.
const CLOSE_TOP = 12;

const FROSTED_BG_WEB = "rgba(255,255,255,0.82)";
const FROSTED_FILTER_WEB = "blur(24px) saturate(1.4)";
// Every centered dialog is 90% of a phone's width, up to this on a tablet or
// the web; a detail dialog that reads better wider asks for `wide`.
const CENTERED_MAX_WIDTH = { default: 420, wide: 480 } as const;
// The tallest a centered or bottom dialog grows before its content scrolls.
const MAX_DIALOG_HEIGHT = "90%" as const;
// A bottom dialog spans a phone's width; on a tablet or the web it stops
// here and sits centered.
const BOTTOM_MAX_WIDTH = 560;

export function Dialog({
  visible,
  onClose,
  layout = "centered",
  size = "default",
  surface = "frosted",
  animationType,
  closeButton = false,
  closeAccessibilityLabel,
  closeAction,
  dismissOnBackdropTap = true,
  containerStyle: containerStyleOverride,
  surfaceStyle,
  contentStyle,
  backdropTestID,
  backdropPressLabel,
  testID,
  children,
}: Props) {
  const reducedMotion = useReducedMotion();
  const capturePress = usePressCapture("Dialog");
  const insets = useSafeAreaInsets();
  const isCentered = layout === "centered";
  const isFullscreen = layout === "fullscreen";
  const isSolid = surface === "solid";

  const containerStyle = isFullscreen
    ? {
        flex: 1,
        width: "100%" as const,
      }
    : isCentered
      ? {
          width: "90%" as const,
          maxWidth: CENTERED_MAX_WIDTH[size],
          maxHeight: MAX_DIALOG_HEIGHT,
          borderRadius: radii.xl + 4, // 20
          overflow: "hidden" as const,
        }
      : {
          width: "100%" as const,
          maxWidth: BOTTOM_MAX_WIDTH,
          maxHeight: MAX_DIALOG_HEIGHT,
          borderTopLeftRadius: radii.xl,
          borderTopRightRadius: radii.xl,
          overflow: "hidden" as const,
        };

  const innerPadding = isFullscreen
    ? {
        flex: 1,
        paddingTop: spacing.md,
        paddingHorizontal: spacing.xl,
        paddingBottom: spacing.sm,
      }
    : isCentered
      ? {
          paddingTop: spacing.xl + spacing.xl, // 40
          paddingHorizontal: spacing.xl + spacing.sm, // 28
          paddingBottom: spacing.xl + spacing.md, // 32
        }
      : {
          paddingTop: spacing.xl,
          paddingHorizontal: spacing.xl,
          // Keep the last row clear of the home indicator.
          paddingBottom: spacing.xl + spacing.sm + insets.bottom,
        };

  const [besideCloseCount, setBesideCloseCount] = useState(0);
  const setTitleBesideClose = useCallback((beside: boolean) => {
    setBesideCloseCount((count) => Math.max(0, count + (beside ? 1 : -1)));
  }, []);
  const flatContent = StyleSheet.flatten([innerPadding, contentStyle]);
  const contentPaddingTop = Number(
    flatContent.paddingTop ??
      flatContent.paddingVertical ??
      flatContent.padding ??
      0,
  );
  const titleBesideClose = closeButton !== false && besideCloseCount > 0;
  const titleTop = Math.min(contentPaddingTop, TITLE_BESIDE_CLOSE_TOP);
  // Beside a left title, the close comes down level with the middle of its
  // line; it never rises above its usual place, for content that starts at
  // the very top.
  const closeTop = titleBesideClose
    ? Math.max(
        CLOSE_TOP,
        titleTop + (DIALOG_TITLE_LINE_HEIGHT - CLOSE_BUTTON_SIZE) / 2,
      )
    : CLOSE_TOP;

  const dialogBody = (
    <View
      testID={testID}
      // Shrinks with the surface, so a ScrollView inside scrolls instead of
      // pushing the dialog past its maximum height.
      style={[
        isFullscreen ? null : { flexShrink: 1 },
        innerPadding,
        contentStyle,
        titleBesideClose ? { paddingTop: titleTop } : null,
      ]}
    >
      {closeButton !== false && (
        <CloseAffordance
          top={closeTop}
          onPress={onClose}
          accessibilityLabel={closeAccessibilityLabel}
          action={closeAction}
          testID={testID ? `${testID}-close` : undefined}
        />
      )}
      <DialogChromeContext.Provider
        value={{
          closeButtonShown: closeButton !== false,
          setTitleBesideClose,
        }}
      >
        {children}
      </DialogChromeContext.Provider>
    </View>
  );

  // Fullscreen fills to the device edges, so keep content clear of the notch
  // and home indicator with a safe-area frame.
  const innerContent = isFullscreen ? (
    <SafeAreaView style={{ flex: 1 }} edges={["top", "bottom"]}>
      {dialogBody}
    </SafeAreaView>
  ) : (
    dialogBody
  );

  return (
    <Modal
      visible={visible}
      transparent
      // The entrance is the platform's own animation, so the preference has to
      // suppress it here: a caller that asked for a slide cannot opt out of
      // honouring it.
      animationType={
        reducedMotion
          ? "none"
          : (animationType ?? (isCentered ? "fade" : "slide"))
      }
      onRequestClose={onClose}
      // On Android the backdrop also covers the status bar.
      statusBarTranslucent
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{
          flex: 1,
          backgroundColor: isFullscreen ? "transparent" : "rgba(0,0,0,0.4)",
          justifyContent: isCentered ? "center" : "flex-end",
          alignItems: isFullscreen ? "stretch" : "center",
        }}
      >
        {dismissOnBackdropTap && (
          <Pressable
            onPress={() => {
              if (backdropPressLabel) capturePress(backdropPressLabel);
              onClose();
            }}
            testID={backdropTestID}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          />
        )}
        <View
          style={[
            containerStyle,
            Platform.OS === "web" ? { cursor: "auto" } : null,
            containerStyleOverride,
          ]}
        >
          {isSolid ? (
            <View
              style={[
                {
                  backgroundColor: semanticColors.surface.default,
                  maxHeight: "100%",
                },
                isFullscreen ? { flex: 1 } : { flexShrink: 1 },
                surfaceStyle,
              ]}
            >
              {innerContent}
            </View>
          ) : Platform.OS === "web" ? (
            <View
              style={[
                {
                  backgroundColor: FROSTED_BG_WEB,
                  backdropFilter: FROSTED_FILTER_WEB,
                  WebkitBackdropFilter: FROSTED_FILTER_WEB,
                  maxHeight: "100%",
                },
                isFullscreen ? { flex: 1 } : { flexShrink: 1 },
                surfaceStyle,
              ]}
            >
              {innerContent}
            </View>
          ) : (
            <BlurView
              intensity={60}
              tint="light"
              style={[
                {
                  backgroundColor: "rgba(255,255,255,0.75)",
                  maxHeight: "100%",
                },
                isFullscreen ? { flex: 1 } : { flexShrink: 1 },
                surfaceStyle,
              ]}
            >
              {innerContent}
            </BlurView>
          )}
        </View>
      </KeyboardAvoidingView>
      {/* A toast raised while the dialog is open shows above it, not under
          its backdrop. */}
      {visible ? <ToastHost /> : null}
    </Modal>
  );
}

function CloseAffordance({
  top,
  onPress,
  accessibilityLabel,
  action,
  testID,
}: {
  top: number;
  onPress: () => void;
  accessibilityLabel?: string;
  action?: string;
  testID?: string;
}) {
  return (
    <CloseButton
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      action={action}
      testID={testID}
      style={{
        position: "absolute",
        top,
        right: 12,
        zIndex: 1,
      }}
    />
  );
}
